/**
 * useSPLHDataV2 Hook
 *
 * Custom React hook for fetching and managing SPLH Dashboard data with comparison support.
 * Handles data fetching, loading states, error handling, debouncing, and AI insights.
 *
 * Architecture:
 * - Fetches data via splhDataServiceV2 when clientId, dateRange, or comparisonMode changes
 * - Debounces changes (300ms) to prevent excessive requests
 * - Returns ready-to-render comparison-aware data from backend
 * - Fetches AI insights in parallel (non-blocking)
 * - Provides refetch function for manual refresh
 *
 * @module pages/common/dashboards/SPLHDashboard/hooks/useSPLHDataV2
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchSPLHDashboardDataV2,
  fetchAIInsights,
  clearCacheV2,
  DEFAULT_COMPARISON_MODE,
  COMPARISON_MODES,
} from '../services/splhDataServiceV2';
import * as logger from '../../../../../utils/logger';

/**
 * Debounce delay in milliseconds
 * @constant {number}
 */
const DEBOUNCE_DELAY = 300;

/**
 * Hook state structure
 * @typedef {Object} UseSPLHDataV2State
 * @property {string} comparisonMode - Current comparison mode
 * @property {Object} currentRange - Current period date range
 * @property {Object} comparisonRange - Comparison period date range
 * @property {Array} kpis - KPI tiles with MetricValue structure
 * @property {Array} trendData - Trend data points with dual series
 * @property {Array} hierarchyData - Hierarchy nodes with comparison metrics
 * @property {Object} breakdownData - Breakdown data with comparison
 * @property {Object} alerts - Grouped alerts by metric and severity
 * @property {Object} hierarchyMetadata - Hierarchy configuration
 * @property {Object} aiInput - Pre-aggregated AI input for insights call
 * @property {Object} aiInsights - AI-generated insights
 * @property {boolean} loading - Whether dashboard data is loading
 * @property {boolean} aiLoading - Whether AI insights are loading
 * @property {string|null} error - Error message if fetch failed
 */

/**
 * Custom hook for fetching SPLH Dashboard data with comparison support
 *
 * @param {string} clientId - Client identifier (e.g., 'dem50')
 * @param {Object} dateRange - Date range for the query
 * @param {string} dateRange.startDate - Start date in YYYY-MM-DD format
 * @param {string} dateRange.endDate - End date in YYYY-MM-DD format
 * @param {string} [comparisonMode='PREVIOUS_PERIOD'] - Comparison mode
 * @returns {Object} Dashboard data, state, and actions
 *
 * @example
 * const {
 *   kpis,
 *   trendData,
 *   hierarchyData,
 *   alerts,
 *   aiInsights,
 *   loading,
 *   error,
 *   comparisonMode,
 *   setComparisonMode,
 *   refetch
 * } = useSPLHDataV2('dem50', { startDate: '2025-11-18', endDate: '2025-11-24' });
 */
export function useSPLHDataV2(clientId, dateRange, initialComparisonMode = DEFAULT_COMPARISON_MODE) {
  // State for comparison mode
  const [comparisonMode, setComparisonMode] = useState(initialComparisonMode);

  // State for comparison context from response
  const [currentRange, setCurrentRange] = useState(null);
  const [comparisonRange, setComparisonRange] = useState(null);

  // State for dashboard data
  const [kpis, setKpis] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [breakdownData, setBreakdownData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [hierarchyMetadata, setHierarchyMetadata] = useState(null);
  const [aiInput, setAiInput] = useState(null);

  // State for AI insights (separate loading state)
  const [aiInsights, setAiInsights] = useState({
    summary: '',
    available: false,
    errorMessage: null,
    generatedAt: null,
  });
  const [aiLoading, setAiLoading] = useState(false);

  // State for loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs for tracking mounted state and race conditions
  const isMountedRef = useRef(true);
  const debounceTimeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  /**
   * Fetches AI insights using pre-aggregated data
   * Called after dashboard data is successfully fetched
   *
   * @param {Object} aiInputData - AI input from dashboard response
   * @private
   */
  const fetchAIInsightsData = useCallback(async (aiInputData) => {
    if (!aiInputData || !isMountedRef.current) return;

    setAiLoading(true);

    try {
      const insights = await fetchAIInsights(aiInputData);

      if (isMountedRef.current) {
        setAiInsights(insights);
      }
    } catch (err) {
      logger.warn('AI insights fetch failed (non-blocking)', { error: err.message });
      if (isMountedRef.current) {
        setAiInsights({
          summary: '',
          available: false,
          errorMessage: 'AI insights are temporarily unavailable.',
          generatedAt: new Date().toISOString(),
        });
      }
    } finally {
      if (isMountedRef.current) {
        setAiLoading(false);
      }
    }
  }, []);

  /**
   * Fetches dashboard data from the backend
   *
   * @param {boolean} [skipCache=false] - Whether to skip the cache
   * @private
   */
  const fetchData = useCallback(
    async (skipCache = false) => {
      if (!clientId || !dateRange?.startDate || !dateRange?.endDate) {
        logger.debug('useSPLHDataV2: Skipping fetch - missing required inputs', {
          clientId,
          dateRange,
        });
        return;
      }

      const currentRequestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      logger.debug('useSPLHDataV2: Fetching data', {
        clientId,
        dateRange,
        comparisonMode,
        skipCache,
        requestId: currentRequestId,
      });

      try {
        const data = await fetchSPLHDashboardDataV2(clientId, dateRange, comparisonMode, { skipCache });

        if (!isMountedRef.current) {
          logger.debug('useSPLHDataV2: Component unmounted, discarding response');
          return;
        }

        if (currentRequestId !== requestIdRef.current) {
          logger.debug('useSPLHDataV2: Stale response, discarding', {
            currentRequestId,
            latestRequestId: requestIdRef.current,
          });
          return;
        }

        // Update all state with the response data
        setCurrentRange(data.currentRange || null);
        setComparisonRange(data.comparisonRange || null);
        setKpis(data.kpis || []);
        setTrendData(data.trendData || []);
        setHierarchyData(data.hierarchyData || []);
        setBreakdownData(data.breakdownData || null);
        setAlerts(data.alerts || null);
        setHierarchyMetadata(data.hierarchyMetadata || null);
        setAiInput(data.aiInput || null);
        setError(null);

        // Complete debug logging of the query response
        console.log('[useSPLHDataV2] Complete query response:', JSON.stringify(data, null, 2));

        logger.debug('useSPLHDataV2: Data fetched successfully', {
          clientId,
          dateRange,
          comparisonMode,
          requestId: currentRequestId,
        });

        // Fetch AI insights in parallel (non-blocking)
        if (data.aiInput) {
          fetchAIInsightsData(data.aiInput);
        }
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) {
          return;
        }

        logger.error('useSPLHDataV2: Fetch failed', {
          clientId,
          dateRange,
          comparisonMode,
          error: err.message,
          requestId: currentRequestId,
        });

        setError(err.message || 'Failed to load dashboard data');
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [clientId, dateRange, comparisonMode, fetchAIInsightsData]
  );

  /**
   * Debounced fetch function
   * Waits for DEBOUNCE_DELAY ms after the last call before fetching
   *
   * @private
   */
  const debouncedFetch = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchData(false);
    }, DEBOUNCE_DELAY);
  }, [fetchData]);

  /**
   * Manual refetch function
   * Clears cache and fetches fresh data immediately (no debounce)
   */
  const refetch = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (clientId) {
      clearCacheV2(clientId);
    }

    fetchData(true);
  }, [clientId, fetchData]);

  /**
   * Refetch AI insights only
   */
  const refetchAI = useCallback(() => {
    if (aiInput) {
      fetchAIInsightsData(aiInput);
    }
  }, [aiInput, fetchAIInsightsData]);

  // Effect to fetch data when clientId, dateRange, or comparisonMode changes
  useEffect(() => {
    debouncedFetch();

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [debouncedFetch]);

  // Effect to track mounted state
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // Comparison context
    comparisonMode,
    setComparisonMode,
    currentRange,
    comparisonRange,

    // Data
    kpis,
    trendData,
    hierarchyData,
    breakdownData,
    alerts,
    hierarchyMetadata,
    aiInput,

    // AI Insights
    aiInsights,
    aiLoading,

    // State
    loading,
    error,

    // Actions
    refetch,
    refetchAI,
  };
}

// Export comparison modes for use in components
export { COMPARISON_MODES, DEFAULT_COMPARISON_MODE };

export default useSPLHDataV2;
